import { useState } from 'react'
import { useQuery } from '@apollo/client'
import { Link } from 'react-router-dom'
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  FolderIcon,
  EyeIcon,
  PencilIcon,
  ChevronRightIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline'
import { GET_CATEGORIES, GET_CATEGORY_TREE } from '../graphql/product-queries'
import { Category, CategoryTreeNode } from '../types/product'
import LoadingSpinner from '../components/LoadingSpinner'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'

const CategoriesPage = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedCategories, setExpandedCategories] = useState<string[]>([])
  const [viewMode, setViewMode] = useState<'tree' | 'list'>('tree')
  const { hasPermission } = usePermissions()

  const canCreate = hasPermission(PERMISSIONS.CATEGORY_CREATE)
  const canUpdate = hasPermission(PERMISSIONS.CATEGORY_UPDATE)

  const { data: categoriesData, loading: categoriesLoading } = useQuery(GET_CATEGORIES)
  const { data: treeData, loading: treeLoading } = useQuery(GET_CATEGORY_TREE)

  const categories = categoriesData?.categories || []
  const categoryTree = treeData?.categoryTree || []

  const handleSearch = (value: string) => {
    setSearchTerm(value)
  }

  const toggleCategoryExpansion = (categoryId: string) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    )
  }

  const filteredCategories = categories.filter((category: any) =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const renderCategoryTree = (nodes: CategoryTreeNode[], level = 0) => {
    return nodes.map(node => {
      const isExpanded = expandedCategories.includes(node.id.toString())
      const hasChildren = node.children && node.children.length > 0

      return (
        <div key={node.id}>
          <div 
            className={`flex items-center p-3 hover:bg-gray-50 cursor-pointer ${
              level > 0 ? 'ml-6' : ''
            }`}
            onClick={() => hasChildren && toggleCategoryExpansion(node.id.toString())}
          >
            {hasChildren ? (
              <button className="mr-2 text-gray-400 hover:text-gray-600">
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </button>
            ) : (
              <div className="w-6 mr-2" />
            )}
            
            <FolderIcon className="h-5 w-5 text-gray-400 mr-3" />
            
            <div className="flex-1">
              <span className="font-medium text-gray-900">{node.name}</span>
            </div>

            <div className="flex gap-2">
              <Link
                to={`/categories/${node.id}`}
                className="p-1 text-gray-400 hover:text-gray-600"
                onClick={(e) => e.stopPropagation()}
                title="View details"
              >
                <EyeIcon className="h-4 w-4" />
              </Link>
              {canUpdate && (
                <Link
                  to={`/categories/${node.id}/edit`}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  onClick={(e) => e.stopPropagation()}
                  title="Edit category"
                >
                  <PencilIcon className="h-4 w-4" />
                </Link>
              )}
            </div>
          </div>

          {hasChildren && isExpanded && (
            <div className="border-l border-gray-200 ml-3">
              {renderCategoryTree(node.children!, level + 1)}
            </div>
          )}
        </div>
      )
    })
  }

  if (categoriesLoading || treeLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Categories</h1>
          <p className="mt-1 text-sm text-gray-500">
            Organize your products into categories and subcategories
          </p>
        </div>
        {canCreate && (
          <Link
            to="/categories/new"
            className="btn-primary"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Category
          </Link>
        )}
      </div>

      {/* Search and View Mode */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* View Mode */}
          <div className="flex border border-gray-300 rounded-lg">
            <button
              onClick={() => setViewMode('tree')}
              className={`px-3 py-2 ${viewMode === 'tree' ? 'bg-primary-500 text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              Tree View
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 ${viewMode === 'list' ? 'bg-primary-500 text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              List View
            </button>
          </div>
        </div>
      </div>

      {/* Categories Content */}
      {viewMode === 'tree' ? (
        <div className="card">
          <div className="divide-y divide-gray-200">
            {renderCategoryTree(categoryTree)}
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Parent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Products
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Subcategories
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCategories.map((category: Category) => (
                  <tr key={category.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <FolderIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{category.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {category.parent?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {category.products?.length || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {category.children?.length || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <Link
                          to={`/categories/${category.id}`}
                          className="text-primary-600 hover:text-primary-900"
                          title="View details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </Link>
                        {canUpdate && (
                          <Link
                            to={`/categories/${category.id}/edit`}
                            className="text-primary-600 hover:text-primary-900"
                            title="Edit category"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredCategories.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📁</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No categories found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm
              ? 'Try adjusting your search'
              : 'Get started by creating your first category'
            }
          </p>
          {!searchTerm && canCreate && (
            <Link to="/categories/new" className="btn-primary">
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Category
            </Link>
          )}
        </div>
      )}
    </div>
  )
}

export default CategoriesPage
